import { extend, localize } from 'vee-validate'
import {
  required as ruleRequired,
  email as ruleEmail,
  min as rule_min,
  max as rule_max,
  min_value as rule_min_value,
  max_value as rule_max_value,
  confirmed as ruleConfirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  alpha_spaces as rule_alpha_spaces,
  integer as rule_integer,
  numeric as rule_numeric,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
  ext as rule_ext,
  size as rule_size,
} from 'vee-validate/dist/rules'
import id from 'vee-validate/dist/locale/id.json'

// eslint-disable-next-line object-curly-newline
import {
  validatorPositive,
  validatorUrlValidator,
  validatorPassword,
  validatorCreditCard,
  validatorResetpasswordRules,
  validatorBeforeTime,
  validatorAfterTime,
} from './validators'

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

export const required = extend('required', ruleRequired)

export const email = extend('email', ruleEmail)

export const min = extend('min', rule_min)

export const max = extend('max', rule_max)

export const minValue = extend('min_value', rule_min_value)

// eslint-disable-next-line
export const max_value = extend('max_value', rule_max_value)

export const confirmed = extend('confirmed', ruleConfirmed)

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

// eslint-disable-next-line
export const alpha_spaces = extend('alpha_spaces', rule_alpha_spaces)

export const integer = extend('integer', rule_integer)

export const numeric = extend('numeric', rule_numeric)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)
// eslint-disable-next-line
export const alpha_num = extend('alpha_num', rule_alpha_num)

export const length = extend('length', rule_length)

export const ext = extend('ext', rule_ext)

export const size = extend('size', rule_size)

export const requiredTrim = extend('required_trim', {
  validate(value) {
    return !!value && value.trim().length > 0
  },
  message: 'Masukkan {_field_} yang valid.',
  computesRequired: true,
})

export const alphanumericSpace = extend('alphanumeric_space', {
  validate(value) {
    const regexList = /^(?!\s+$)[a-zA-Z0-9\s]+$/
    return regexList.test(value)
  },
  message: 'Masukkan {_field_} yang valid.',
})

export const minMaxLength = extend('min_max_length', {
  validate(value, { min: minVal, max: maxVal }) {
    return value.length >= minVal && value.length <= maxVal
  },
  params: ['min', 'max'],
  message: (fieldName, placeholders) => {
    const capField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
    return `${capField} harus terdiri dari ${placeholders.min}–${placeholders.max} karakter.`
  },
})

export const uniqueValue = extend('unique_value', {
  validate(value, { validateFn }) {
    if (!value) return true
    if (typeof validateFn !== 'function') return true
    return validateFn(value)
  },
  params: ['validateFn'],
  message: '{_field_} sudah digunakan, silakan gunakan yang lain.',
})

export const noLeadingTrailingSpaces = extend('no_leading_trailing_spaces', {
  validate(value) {
    if (!value) return true
    const str = String(value)
    return str === str.trim()
  },
  message: '{_field_} tidak boleh diawali atau diakhiri dengan spasi',
})

export const noConsecutiveSpaces = extend('no_consecutive_spaces', {
  validate(value) {
    if (!value) return true
    return !/\s{2,}/.test(String(value))
  },
  message: '{_field_} tidak boleh menggunakan spasi lebih dari satu kali secara berurutan',
})

export const positive = extend('positive', {
  validate: validatorPositive,
  message: 'Please enter positive number!',
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: 'It is not valid credit card!',
})

export const password = extend('password', {
  validate: validatorPassword,
  message:
    'Your {_field_} must contain at least one uppercase, one lowercase, one special character and one digit',
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: 'URL is invalid',
})

// Reset Password
export const resetpasswordrequired = extend('resetpasswordrequired', {
  ...ruleRequired,
  message: 'Masukan password baru Kamu.',
})
export const resetconfirmpasswordrequired = extend(
  'resetconfirmpasswordrequired',
  {
    ...ruleRequired,
    message: 'Konfirmasi password baru Kamu.',
  },
)
export const resetpasswordconfirm = extend('resetpasswordconfirm', {
  ...ruleConfirmed,
  message: 'Maaf, password tidak sesuai.',
})
export const resetpasswordrules = extend('resetpasswordrules', {
  validate: validatorResetpasswordRules,
  message: 'Terdiri dari 8-16 karakter, mengandung dari huruf dan angka.',
})

// Forgot Password
export const forgotpasswordEmail = extend('forgotpasswordEmail', {
  ...ruleEmail,
  message: 'Format email yang Kamu masukan salah.',
})

// Work Mode
export const beforeTime = extend('beforeTime', {
  params: ['target'],
  validate: validatorBeforeTime,
  message: '{_field_} harus lebih awal dari {target}',
})

export const afterTime = extend('afterTime', {
  params: ['target'],
  validate: validatorAfterTime,
  message: '{_field_} harus lebih akhir dari {target}',
})

/**
 * Helper function to extract a file object from an array or single value.
 * @param {File|File[]} val - The file(s) input from the validation field.
 * @returns {File} The first file object.
 */
const getFile = val => (Array.isArray(val) ? val[0] : val)

/**
 * Validates that an image file does not exceed a maximum size in Megabytes (MB).
 * @param {File|File[]} val - The file to be validated.
 * @param {Object} params - The validation parameters.
 * @param {number|string} params.maxSize - Maximum allowed size in MB.
 * @returns {boolean} True if the size is within the limit, false otherwise.
 */
export const imageMaxSize = extend('image_max_size', {
  params: ['maxSize'],
  validate: (val, { maxSize }) => {
    const f = getFile(val)
    return !f || !(f instanceof File) || f.size <= (Number(maxSize) || 1) * 1048576
  },
  message: 'Ukuran file maksimal {maxSize}Mb',
})

/**
 * Validates that an image file has exactly the specified width and height.
 * @param {File|File[]} val - The file to be validated.
 * @param {Object} params - The validation parameters.
 * @param {number|string} params.width - Exact required width in pixels.
 * @param {number|string} params.height - Exact required height in pixels.
 * @returns {Promise<boolean>|boolean} A promise resolving to true if dimensions match exactly.
 */
export const imageDimensions = extend('image_dimensions', {
  params: ['width', 'height'],
  validate: (val, { width, height }) => {
    const f = getFile(val)
    if (!f || !(f instanceof File)) return true
    const w = Number(width) || 1200
    const h = Number(height) || 360
    return new Promise(resolve => {
      const img = new Image()
      img.src = URL.createObjectURL(f)
      img.onload = () => {
        resolve(img.width === w && img.height === h)
        URL.revokeObjectURL(img.src)
      }
      img.onerror = () => {
        resolve(false)
        URL.revokeObjectURL(img.src)
      }
    })
  },
  message: 'Ukuran gambar harus {width}x{height}px',
})

/**
 * Validates that an image file matches a specific aspect ratio.
 * Uses cross-multiplication (width * heightRatio === height * widthRatio) to avoid floating-point inaccuracies.
 * @param {File|File[]} val - The file to be validated.
 * @param {Object} params - The validation parameters.
 * @param {number|string} params.width - The width ratio unit (e.g., 16 for 16:9).
 * @param {number|string} params.height - The height ratio unit (e.g., 9 for 16:9).
 * @returns {Promise<boolean>|boolean} A promise resolving to true if the aspect ratio matches.
 */
export const imageRatio = extend('image_ratio', {
  params: ['width', 'height'],
  validate: (val, { width, height }) => {
    const f = getFile(val)
    if (!f || !(f instanceof File)) return true
    const w = Number(width) || 1
    const h = Number(height) || 1
    return new Promise(resolve => {
      const img = new Image()
      img.src = URL.createObjectURL(f)
      img.onload = () => {
        resolve(img.width * h === img.height * w)
        URL.revokeObjectURL(img.src)
      }
      img.onerror = () => {
        resolve(false)
        URL.revokeObjectURL(img.src)
      }
    })
  },
  message: 'Rasio gambar harus {width}:{height}',
})

// Install English and Arabic localizations.
localize('id', {
  messages: id.messages,
})
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
